import { Suspense } from 'react';
import type { ReactNode } from 'react';
import RequireRole from './RequireRole';
import AdminLayout, { type AdminLayoutActiveKey } from './AdminLayout';

/**
 * Collapses the RequireRole > AdminLayout > (optional Suspense) wrapper that
 * every /admin/* route hand-rolled ~20 times (audit: frontend-app-shell:1,
 * bd-ozirv.10). Pure structural collapse — role gate, admin chrome, and (for
 * lazy pages) a Suspense boundary — in one call.
 *
 * `fallback` is intentionally NOT defaulted: pass it only for lazy pages, the
 * same ones that had a Suspense boundary before this refactor. Omitting it
 * skips the Suspense wrapper entirely, matching the eagerly-imported admin
 * pages (AdminMapping, AdminUsers, QueuePage, etc.) that never had one — a
 * default fallback would add an inert-but-unnecessary Suspense boundary
 * around a component that can never suspend. See
 * AppRouter.adminRoutes.test.tsx for the route-by-route pin.
 */
export default function AdminRoute({
  roles,
  activeKey,
  fallback,
  children,
}: {
  roles: string[];
  activeKey?: AdminLayoutActiveKey;
  fallback?: ReactNode;
  children: ReactNode;
}) {
  return (
    <RequireRole anyOf={roles}>
      <AdminLayout activeKey={activeKey}>
        {fallback !== undefined ? <Suspense fallback={fallback}>{children}</Suspense> : children}
      </AdminLayout>
    </RequireRole>
  );
}
